import React, {useEffect, useState} from "react";
import {Map, Marker, useMap} from "@vis.gl/react-google-maps";
import styles from './SolarMap.module.css';
import {getGeoJsonUrl, getProjectData} from "../../lib/Solar/SolarApiClient.ts";
import type {MapCenter} from "./SolarGoogleMap.tsx";
import {getPanelStates, initPanels} from "../../lib/Solar/PanelManager.ts";
import type {ApiErrorResponse} from "@shared/interfaces/apiError";
import type {SlimmedProjectGetDataResponse} from "@shared/interfaces/solar";
import {createLogger} from "../../lib/logger.ts";

const logger = createLogger("SolarMap");

type SolarMapProps = {
    location: { lat: number, lng: number };
    markerPosition: MapCenter;
    projectId?: number;
    currentModuleCount?: number;
    isDraggable?: boolean;
    setModuleCount?: (count: number) => void;
    showHiddenPanels?: boolean;
    // activeSegment: any;
    // setActiveSegment: (segment: any) => void;
    // handleMapClick: (latLng: google.maps.LatLng) => void;
    // overlayUrl: string | null;
    // overlayBounds: google.maps.LatLngBoundsLiteral | null;
};

const getCenterPosition = (markerPosition: MapCenter, location: { lat: number; lng: number }) => {
    return markerPosition.doesMarkerEqualsLocation ? markerPosition.coordinates : location;
}
const SolarMap: React.FC<SolarMapProps> = ({
    location,
    markerPosition,
    projectId,
    currentModuleCount,
    isDraggable = false,
    // setModuleCount,
    showHiddenPanels = false,
    // activeSegment,
    // setActiveSegment,
    // handleMapClick,
    // overlayUrl,
    // overlayBounds,
}) => {
    const centerPosition = getCenterPosition(markerPosition, location);
    const map = useMap();
    const [projectData, setProjectData] = useState<SlimmedProjectGetDataResponse | ApiErrorResponse>();
    // let panelClickListener: google.maps.MapsEventListener | null = null;

    useEffect(() => {
        if (!map) return;

        map.setCenter(getCenterPosition(markerPosition, location));
    }, [markerPosition, location, map]);

    useEffect(() => {
        if (!map) return;
        if (!projectId) return;

        const bounds = new google.maps.LatLngBounds();

        map.data.loadGeoJson(getGeoJsonUrl(projectId).toString(), null, (features) => {
            features.forEach((feature) => {
                feature.getGeometry()?.forEachLatLng((latLng) => {
                    bounds.extend(latLng);
                });
            });

            map.fitBounds(bounds);
            map.setCenter(bounds.getCenter());
            logger.debug('Center position', { center: bounds.getCenter().toJSON(), mapCenter: map.getCenter()?.toJSON(), span: bounds.toSpan().toJSON() });
        });

        map.data.setStyle(((feature) => {
            switch (feature.getProperty('featureRole')) {
                case 'panelInstallationArea':
                    return {visible: false, clickable: false};
                case 'panelCell':
                    return {visible: true, fillColor: '#0000ff', strokeWeight: 0.5, fillOpacity: 0.5};
                default:
                    logger.debug('Unknown feature', { feature });
                    break;
            }
        }) as google.maps.Data.StylingFunction);

        getProjectData(projectId).then((data) => {
            logger.debug('Project data', { data });
            setProjectData(data);
        });
    }, [map, projectId]);

    // if (map) {
    //     map.data.addListener('click', (event) => {
    //         debugger;
    //         const currentFeature = event.feature as Feature;
    //         currentFeature.setProperty('active', !currentFeature.getProperty('active'));
    //         map.data.overrideStyle(event.feature, currentFeature.getProperty('active') ? {
    //             fillColor: '#00f',
    //             fillOpacity: 0.5
    //         } : {fillColor: '#ccc', fillOpacity: 0.5});
    //
    //         if (setModuleCount && currentModuleCount) {
    //             setModuleCount(currentModuleCount + (currentFeature.getProperty('active') ? 1 : -1));
    //         }
    //     })
    // }

    useEffect(() => {
        if (!projectData) return;
        if (!currentModuleCount) return;
        if (!map) return;

        if (!('confirmedPanelsData' in projectData)) {
            logger.error('ConfirmedPanelsData invalid', { projectId });
            return;
        }
        initPanels(projectData.confirmedPanelsData, projectData.confirmedRooftopData);
        logger.debug('Initialized panels with project data', { confirmedPanelsData: projectData.confirmedPanelsData, confirmedRooftopData: projectData.confirmedRooftopData, projectData });
        const panelStates = getPanelStates(currentModuleCount);

        map.data.forEach((feature) => {
            if (feature.getProperty('featureRole') === 'panelCell') {
                feature.setProperty('active', panelStates.find((panel) => panel === feature.getProperty('id')));
            }
        });

        map.data.setStyle((feature) => {
            if (feature.getProperty('featureRole') === 'panelCell') {
                if (panelStates.find((panel) => panel === feature.getProperty('id'))) {
                    return {visible: true, fillColor: '#0000ff', strokeWeight: 0.5, fillOpacity: 0.5};
                }

                return {visible: showHiddenPanels, fillColor: '#ccc', strokeWeight: 0.5, fillOpacity: 0.5};
            }

            return {visible: false, clickable: false};
        });

    }, [projectData, currentModuleCount, showHiddenPanels, map]);

    return <Map
        className={styles.mapView} // Necessary for border radius
        {...(isDraggable ? {defaultCenter: centerPosition} : {defaultCenter: centerPosition})}

        defaultZoom={20}
        minZoom={19}
        maxZoom={21}
        zoomControl={true}

        gestureHandling={isDraggable ? "auto" : "none"}
        scaleControl={true}
        disableDefaultUI={true}
        mapTypeId={'hybrid'}
        tilt={0}
        // defaultBounds={boundingBox}
        defaultTilt={0}
        tiltInteractionEnabled={false}
        reuseMaps={true}
        heading={0}
        // mapContainerStyle={containerStyle}
        // mapTypeId="satellite"
        // onClick={(e) => handleMapClick(e.latLng)}
        // onLoad={handleMapLoad}
    >
        {markerPosition && (
            // @todo Deprecated, use AdvancedMarker (needs unique mapID first)
            <Marker position={markerPosition.coordinates}/>
        )}
    </Map>;
}

export default SolarMap;